import { produce } from 'immer'
import { create } from 'zustand'

import { Doctor, FormattedSlot, Location } from '@/apiTypes'
import { groupSlotsByDate } from '@/utils/groupSlotsByDate.ts'

type Embed = 'app' | 'web'
type ReferralData = {
  location: string
  type: string
  zdCode: string
}

type PatientData = {
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  email?: string
  phone?: string
  initials?: string
  id?: string
  gender?: string
}

type BMIData = {
  height: number
  weight: number
  calculatedBmi?: number
}

interface SortedSlotGroup {
  date: string
  slots: FormattedSlot[]
}

interface Questionnaire {
  answers: { text: string; value: string; id: string }[]
  ids: string[]
  question: string
}

interface AppointmentStore {
  error?: string
  embed: Embed
  slots: FormattedSlot[]
  sortedSlots: SortedSlotGroup[]
  zdCode?: string
  questionnaire?: Questionnaire
  selectedLocation?: Location
  defaultLocationId?: string
  appointmentType?: string
  selectedSlot?: FormattedSlot
  setReferralData: (value: ReferralData) => void
  setReferralCode: (code: string) => void
  setAppointmentType: (type: string) => void
  setEmbed: (embed: Embed) => void
  setSlots: (slots: FormattedSlot[]) => void
  setError: (error: string) => void
  setSelectedLocation: (location: Location) => void
  setDefaultLocationId: (locationEmmaId: string) => void
  setSelectedSlot: (slot?: FormattedSlot) => void
  setQuestionnaire: (questionnaire: Questionnaire) => void
  getSelectedSlot: () => FormattedSlot | undefined
  getSortedSlots: () => SortedSlotGroup[]
  getDoctorsByLocation: () => Doctor[]
  getSlotsByDoctor: (doctorEmmaId?: string) => SortedSlotGroup[]
  resetStore: () => void
  patientData?: PatientData
  setPatientData: (patientData: PatientData) => void
  bmiData?: BMIData
  setBMIData: (bmiData: BMIData) => void
}

const useAppointmentStore = create<AppointmentStore>((set, get) => ({
  embed: window.isWebView ? 'app' : 'web',
  error: undefined,
  getDoctorsByLocation: () => {
    const { selectedLocation, slots } = get()
    const doctorsMap = new Map<string, Doctor>()

    slots.forEach((slot) => {
      if (slot.location.emmaId === selectedLocation?.emmaId) {
        const { emmaId, id, name, picture, shortDescription, title } =
          slot.practitioner
        if (!doctorsMap.has(emmaId)) {
          doctorsMap.set(emmaId, {
            emmaId,
            id,
            name,
            picture,
            shortDescription,
            title,
          })
        }
      }
    })

    return Array.from(doctorsMap.values())
  },
  getSelectedSlot: () => get().selectedSlot,
  getSlotsByDoctor: (doctorEmmaId?: string) => {
    const { slots } = get()

    if (!doctorEmmaId) {
      return []
    }

    const filteredSlots = slots.filter(
      (slot) => slot.practitioner.emmaId === doctorEmmaId,
    )

    return groupSlotsByDate(filteredSlots)
  },
  getSortedSlots: () => get().sortedSlots,
  manualBooking: undefined,
  questionnaire: undefined,
  referralData: undefined,
  resetStore: () => {
    set(
      produce((state) => {
        state.contactData = undefined
        state.embed = window.isWebView ? 'app' : 'web'
        state.referralData = undefined
        state.slots = []
        state.sortedSlots = []
        state.locations = undefined
        state.error = undefined
        state.selectedSlot = undefined
        state.manualBooking = undefined
        state.questionnaire = undefined
      }),
    )
  },
  selectedSlot: undefined,
  setAppointmentType: (type: string) =>
    set(
      produce((state) => {
        state.appointmentType = type
      }),
    ),
  setBMIData: (bmiData) =>
    set(
      produce((state) => {
        state.bmiData = bmiData
      }),
    ),
  setDefaultLocationId: (locationEmmaId) => {
    set(
      produce((state) => {
        state.defaultLocationId = locationEmmaId
      }),
    )
  },
  setEmbed: (embed: Embed) => {
    set(
      produce((state) => {
        state.embed = embed
      }),
    )
  },
  setError: (error) => set({ error, slots: [], sortedSlots: [] }),
  setPatientData: (patientData) =>
    set(
      produce((state) => {
        state.patientData = {
          ...state.patientData,
          ...patientData,
        }
      }),
    ),
  setQuestionnaire: (questionnaire) =>
    set(
      produce((state) => {
        state.questionnaire = questionnaire
      }),
    ),
  setReferralCode: (code) =>
    set(
      produce((state) => {
        state.zdCode = code
      }),
    ),
  setReferralData: ({ location, type, zdCode }: ReferralData) =>
    set(
      produce((state) => {
        state.zdCode = zdCode
        state.defaultLocationId = location
        state.appointmentType = type
      }),
    ),
  setSelectedLocation: (location) =>
    set(
      produce((state) => {
        state.selectedLocation = location
      }),
    ),
  setSelectedSlot: (slot) =>
    set(
      produce((state) => {
        state.selectedSlot = slot
      }),
    ),
  setSlots: (slots) =>
    set(
      produce((state) => {
        state.slots = slots
        state.error = undefined
        state.sortedSlots = groupSlotsByDate(slots)
      }),
    ),
  slots: [],
  sortedSlots: [],
}))

export type { ReferralData, SortedSlotGroup }
export { useAppointmentStore }
